import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subject, of } from 'rxjs';
import { takeUntil, takeWhile, delay, tap } from 'rxjs/operators';

/**
 * Services imports.
 */
import { FunCollectionService } from '../../common/common-functions/fun-collection.service';
import { GetService } from '../../dataservices/get.service';
import { AnalyticsEventService } from './../../analytics-event/analytics-event.service';
import { NewLanguageService } from '../../dataservices/new-language/new-language.service';
import { MasterserviceService } from '../../dataservices/master-service/masterservice.service';
import { LocalstorageService } from '../../auth/localstorage.service';

/**
 * Constant imports
 */
import { NUMBERS, PRODUCT_TYPE_CONSTANT, SCREEN_NAME, ERROR_CODES, TARGET_TYPE } from '../../constants/app.constants';
import { EVENT_NAME } from './../../analytics-event/analytics.constant';

/**
 * Interface imports.
 */
import { AnalyticsInterface } from '../../analytics-event/analytics.model';
import { Error } from '../../onboarding/interface/apiResponse.model';
import { Target } from '../model/ncp.model';

@Component({
  selector: 'app-game-screen',
  templateUrl: './game-screen.component.html',
  styleUrls: ['./game-screen.component.scss']
})
export class GameScreenComponent implements OnInit, OnDestroy {

  /**
   * @gameResourceUrl defines Iframe content URL
   * @isFinishe defines a spinner state
   * @ncpId defins NCP product id
   * @notifier$ defines a notifier subject stream to end data streaming
   */
  public gameResourceUrl: SafeResourceUrl;
  public isFinished: boolean;
  private productId: number;
  private notifier$ = new Subject();
  public callingfrom = 'Game detail';
  /**
   * Used to hold api response in case of any error.
   */
  public userData: Error;

  /**
   * used to hold the error code constant.
   */
  public errorCodes = ERROR_CODES;

  /**
   * Analyticsdata object.
   */
  public analyticsData: AnalyticsInterface;
  public sessiontime = { 'start_time': '', 'end_time': '' };

  /**
   * Flag for active in component.
   */
  private componentActive = true;





  constructor(
    private route: ActivatedRoute,
    private getService: GetService,
    private fnCollectionService: FunCollectionService,
    private analyticsEventService: AnalyticsEventService,
    private mls: NewLanguageService,
    private masterService: MasterserviceService,
    private lcs: LocalstorageService) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.productId = params['gameId'];
      if (this.productId) {
        const dateObj = new Date();
        this.sessiontime.start_time = dateObj.getTime().toString();

        this.getGameDetails(this.productId);
      }
    });
  }

  /**
   * @EventHandler to retriev Game details
   * @param productId defines NCP product id
   */
  getGameDetails(productId: number): void {
    this.isFinished = false;
    this.getService.gameDetails(productId).
      pipe(
        takeUntil(this.notifier$)
      ).subscribe(detail => {
        this.isFinished = true;
        if (detail.status === NUMBERS.ONE) {
          const product = detail.data.ncp_product[0];
          const isBannerRedirect = this.lcs.getInLocalStorage('isBannerRedirect');
          if (isBannerRedirect === 'true') {
            if (product.banner_redirection) {
              this.setGameResourceUrl(product.banner_redirection);
            }
            return;
          } else {
            this.lcs.setInLocalstorage('isBannerRedirect', false);
            this.setGameResourceUrl(product.target);
            return;
          }
        } else if (detail.status === NUMBERS.ZERO) {
          if (detail.error.code === this.errorCodes.RESTRICTED_CONTENT_ERROR_CODE || detail.error.code === this.errorCodes.ERROR_1025 || detail.error.code === this.errorCodes.ERROR_1005) {
            this.userData = detail.error;
          }
          this.fnCollectionService.errorHandeler(detail);
        }
      }, () => {
        this.isFinished = true;
      });
  }
  /**
   * set iframe url
   * @param redirect for game resource
   */
  setGameResourceUrl(redirect: Target): void {
    if (this.fnCollectionService.validURL(redirect.url)) {
      const url = this.fnCollectionService.rewriteUrl(redirect.url);
      this.gameResourceUrl = ([TARGET_TYPE.NCP_LINK, TARGET_TYPE.LINK].indexOf(redirect.type) > NUMBERS.MINUS_ONE) ? url : '';
      if (!this.gameResourceUrl) {
        this.fnCollectionService.navigateByLinkType(redirect.type, redirect.url);
      }
    } else {
      this.fnCollectionService.showErrorMessage();
    }
  }

  /**
   * Call the Custom XHR Api in case of window unload(reload, tab and browser close).
   */
  @HostListener('window:beforeunload.out-zone', ['$event'])
  beforeUnloadHandler(): boolean {
    this.callXhrForGameAnalyticEventOnWindowUnLoad();
    return true;
  }

  /**
   * Call the Custom XHR Api in case of window unload(reload, tab and browser close).
   */
  callXhrForGameAnalyticEventOnWindowUnLoad(): void {
    of(true).pipe(
      takeWhile(() => this.componentActive),
      delay(0),
      tap(() => {
        const dateObj = new Date();
        this.sessiontime.end_time = dateObj.getTime().toString();
        this.analyticsData = {
          'event_name': EVENT_NAME.WEBVIEW_NCP_VISIT,
          'product_type': PRODUCT_TYPE_CONSTANT.NCP_SUB_TYPE,
          'product_type_id': this.productId,
          'screen_name': SCREEN_NAME.NCP_SUBTYPE_WEBVIEW,
          'start_time': this.sessiontime.start_time,
          'end_time': this.sessiontime.end_time
        };
        this.analyticsEventService.logAnalyticsEvent(this.analyticsData, true);
      })).subscribe();
  }

  /**
   * @EventHandler to clean up memory/Event handler/subscription
   */
  ngOnDestroy(): void {
    this.componentActive = false;
    this.notifier$.next();
    this.notifier$.complete();
    const dateObj = new Date();
    this.sessiontime.end_time = dateObj.getTime().toString();
    this.analyticsData = {
      'event_name': EVENT_NAME.WEBVIEW_NCP_VISIT,
      'product_type': PRODUCT_TYPE_CONSTANT.NCP_SUB_TYPE,
      'product_type_id': this.productId,
      'screen_name': SCREEN_NAME.NCP_SUBTYPE_WEBVIEW,
      'start_time': this.sessiontime.start_time,
      'end_time': this.sessiontime.end_time
    };
    this.analyticsEventService.logAnalyticsEvent(this.analyticsData);
  }
}
